import { demoToday, shiftDays } from '../../../../../lib/demoTime';

/**
 * 导入任务状态：已上传、还没确认导入 →「待导入」；确认后成功 + 失败没走完总数 →「导入中」；
 * 走完总数 →「已导入」。状态由「是否确认」标志 + 计数派生，不写静态字段。
 */
export type ImportStatus = '已导入' | '待导入' | '导入中';

export interface ImportRecord {
  /**
   * 是否已确认导入。`false` = 文件已上传、还挂在服务端的待导入任务（未确认前不会写库），
   * 因此这一条只给「导入数据 / 删除」，不给「查看结果 / 撤销」。
   */
  readonly confirmed: boolean;
  readonly failed: number;
  readonly fileName: string;
  readonly id: number;
  readonly operator: string;
  readonly succeeded: number;
  /** 模板里的数据行数，是文件的客观输入，不随导入结果变化。 */
  readonly total: number;
  /** 上传时刻，`YYYY-MM-DD HH:mm:ss`。 */
  readonly uploadedAt: string;
}

/** 种子只写「相对今天第几天」与当天时刻；绝对日期在构造时才落地，因此记录不会整批过期。 */
export interface ImportRecordSeed {
  readonly confirmed: boolean;
  readonly dayOffset: number;
  readonly failed: number;
  readonly operator: string;
  readonly succeeded: number;
  readonly time: string;
  readonly total: number;
}

/*
 * 演示用的订单导入记录。头两条是「未走完」的任务，用来演示列表里的三种状态，
 * 其余对齐设计稿样例并按更早的日期补齐，让分页有多页可翻。
 * 种子的顺序就是列表顺序（最新在前）。
 */
export const orderImportSeeds: readonly ImportRecordSeed[] = [
  // 正在导入：已确认导入，成功 448 + 失败 12 还没到 1200 → 状态「导入中」，状态列走进度条。
  { confirmed: true, dayOffset: 0, failed: 12, operator: '詹某某', succeeded: 448, time: '13:58:41', total: 1200 },
  // 已上传、还没确认导入：状态「待导入」，操作只有「导入数据 / 删除」。「删除」会清空上方上传的文件态。
  { confirmed: false, dayOffset: 0, failed: 0, operator: '詹某某', succeeded: 0, time: '09:12:07', total: 1200 },
  { confirmed: true, dayOffset: -2, failed: 0, operator: '詹某某', succeeded: 2, time: '10:28:03', total: 2 },
  { confirmed: true, dayOffset: -4, failed: 3, operator: '詹某某', succeeded: 1, time: '16:00:21', total: 4 },
  { confirmed: true, dayOffset: -4, failed: 3, operator: '内置管理员', succeeded: 0, time: '15:41:29', total: 3 },
  { confirmed: true, dayOffset: -9, failed: 0, operator: '内置管理员', succeeded: 1, time: '15:06:47', total: 1 },
  { confirmed: true, dayOffset: -15, failed: 0, operator: '詹某某', succeeded: 1, time: '16:23:48', total: 1 },
  { confirmed: true, dayOffset: -15, failed: 1, operator: '詹某某', succeeded: 0, time: '16:04:56', total: 1 },
  { confirmed: true, dayOffset: -15, failed: 0, operator: '詹某某', succeeded: 1, time: '16:03:32', total: 1 },
  { confirmed: true, dayOffset: -15, failed: 0, operator: '詹某某', succeeded: 2, time: '10:41:10', total: 2 },
  { confirmed: true, dayOffset: -15, failed: 0, operator: '詹某某', succeeded: 2, time: '10:10:51', total: 2 },
  { confirmed: true, dayOffset: -15, failed: 1, operator: '詹某某', succeeded: 1, time: '09:24:47', total: 2 },
  { confirmed: true, dayOffset: -19, failed: 0, operator: '内置管理员', succeeded: 5, time: '14:02:18', total: 5 },
  { confirmed: true, dayOffset: -24, failed: 0, operator: '詹某某', succeeded: 3, time: '09:31:05', total: 3 },
];

export function deriveImportStatus(record: ImportRecord): ImportStatus {
  if (!record.confirmed) return '待导入';
  return record.succeeded + record.failed >= record.total ? '已导入' : '导入中';
}

/** 已处理笔数占模板总行数的百分比，给「导入中」的进度条用；未开始或除不尽时向下取整。 */
export function deriveImportProgress(record: ImportRecord): number {
  if (record.total <= 0) return 0;
  return Math.min(100, Math.floor(((record.succeeded + record.failed) / record.total) * 100));
}

/** 模板文件名跟着上传时刻走，形态与设计稿的「模板名_年月日时分.xls」一致。 */
function templateFileName(prefix: string, day: string, time: string): string {
  return `${prefix}_${day.replaceAll('-', '')}${time.slice(0, 2)}${time.slice(3, 5)}.xls`;
}

export function buildImportRecords(
  templatePrefix: string,
  seeds: readonly ImportRecordSeed[] = orderImportSeeds,
  today: string = demoToday,
): ImportRecord[] {
  return seeds.map((seed, index) => {
    const day = shiftDays(today, seed.dayOffset);
    return {
      confirmed: seed.confirmed,
      failed: seed.failed,
      fileName: templateFileName(templatePrefix, day, seed.time),
      id: index + 1,
      operator: seed.operator,
      succeeded: seed.succeeded,
      total: seed.total,
      uploadedAt: `${day} ${seed.time}`,
    };
  });
}
